import React from "react";
import PageShell from "@/components/PageShell";
import NextRoom from "@/components/NextRoom";
import { PILLARS, PHILOSOPHY_LENSES, REASONING_MODEL, PORTRAITS } from "@/data/content";
import { Medallion } from "@/components/CompassMark";

export default function Philosophy() {
  return (
    <PageShell
      testId="page-philosophy"
      title="Philosophy | Quentin Young"
      description="The principles, questions, and frameworks behind Quentin Young's work, including responsibility, craft, culture, systems, purpose, and human potential."
      ogImage="/og-philosophy.jpg"
      breadcrumbs={[{ name: "Home", url: "/" }, { name: "Philosophy", url: "/philosophy" }]}
    >
      <section className="container-edge pt-16 md:pt-24 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <p className="eyebrow-gold mb-5">Philosophy</p>
            <h1 className="display-serif text-4xl md:text-6xl lg:text-7xl text-paper-primary text-balance leading-[1.02]">
              Six lenses for evaluating almost anything that matters.
            </h1>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end">
            <Medallion size={220} className="opacity-90" />
          </div>
        </div>
      </section>

      {/* Six Pillars */}
      <section className="bg-ink-base border-t border-ink-line py-20 md:py-28">
        <div className="container-edge">
          <p className="eyebrow mb-8">The Six Pillars</p>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-px bg-ink-line">
            {PILLARS.map((p) => (
              <li key={p.name} id={`pillar-${p.slug}`} className="bg-ink-base p-10 md:p-12 scroll-mt-24" data-testid={`pillar-${p.name.toLowerCase()}`}>
                <div className="flex items-baseline gap-5">
                  <span className="font-mono text-[11px] tracking-[0.24em] text-gold-warm">{p.number}</span>
                  <h2 className="display-serif text-3xl md:text-4xl text-paper-primary">{p.name}</h2>
                </div>
                <p className="mt-5 text-paper-secondary text-lg leading-relaxed">{p.definition}</p>
                <div className="mt-8 grid grid-cols-1 gap-3 border-t border-ink-line pt-6">
                  <div>
                    <p className="eyebrow mb-2">Rejects</p>
                    <p className="text-paper-tertiary text-sm leading-relaxed">{p.rejects}</p>
                  </div>
                  <div>
                    <p className="eyebrow mb-2">Without it</p>
                    <p className="text-paper-tertiary text-sm italic leading-relaxed">{p.absence}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Reasoning Model */}
      <section className="bg-ink-surface border-y border-ink-line py-20 md:py-28">
        <div className="container-edge">
          <p className="eyebrow mb-5">Reasoning Model</p>
          <h2 className="display-serif text-3xl md:text-5xl text-paper-primary text-balance max-w-3xl">
            Purpose → People → Culture → Systems → Leadership → Performance → Evolution.
          </h2>
          <p className="mt-6 max-w-2xl font-serif text-lg text-paper-secondary leading-relaxed text-pretty">
            The sequence used to evaluate a person, team, organization, idea, or opportunity. The order matters more than people think.
          </p>

          <ol className="mt-14 grid grid-cols-1 md:grid-cols-7 gap-px bg-ink-line">
            {REASONING_MODEL.map((s) => (
              <li key={s.step} className="bg-ink-surface p-6 md:p-8 min-h-[180px]">
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold-warm">{s.step}</p>
                <h3 className="mt-3 font-serif text-xl text-paper-primary">{s.name}</h3>
                <p className="mt-3 text-paper-tertiary text-sm leading-relaxed">{s.note}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Lenses */}
      <section className="bg-ink-base py-20 md:py-28">
        <div className="container-edge">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="aspect-[4/5] overflow-hidden border border-ink-line bg-ink-surface">
                <img src={PORTRAITS.threeQuarter} alt="Quentin Young portrait, three-quarter view." className="w-full h-full object-cover img-desaturate" />
              </div>
              <p className="mt-4 eyebrow">Portrait No. 04, Three-Quarter</p>
            </div>
            <div className="lg:col-span-7">
              <p className="eyebrow mb-5">Operating Lenses</p>
              <ul className="space-y-8">
                {PHILOSOPHY_LENSES.map((l, i) => (
                  <li key={l.name} className="border-b border-ink-line pb-8" data-testid={`lens-${i}`}>
                    <div className="flex items-baseline gap-4">
                      <span className="font-mono text-[10px] tracking-[0.24em] text-paper-mute">{String(i + 1).padStart(2, "0")}</span>
                      <h3 className="font-serif text-2xl text-paper-primary">{l.name}</h3>
                    </div>
                    <p className="mt-3 text-paper-secondary leading-relaxed">{l.body}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <NextRoom
        eyebrow="Move to the next room"
        title="A framework is only as good as its evidence."
        rooms={[
          { label: "See the framework in practice", to: "/building", hint: "proof" },
          { label: "Bring this into a room", to: "/speaking", hint: "speaking" },
          { label: "Explore coaching", to: "/coaching", hint: "direct work" },
        ]}
        testId="philosophy-next"
      />
    </PageShell>
  );
}
